import { Outlet } from 'react-router'
import { Header, HeaderSpacer } from './Header'
import { BackBar } from './BackBar'
import { useThemeColor } from '../hooks/StatusBarColorPicker'
import { useI18n, type MessageKey } from '../i18n'

/**
 * Layout for standalone (non-tab) authenticated screens: the fixed EDF header, a
 * back row, and safe-area-aware content — but no bottom tab bar. Used as a route
 * layout so drill-in pages (the application form, profile, generic form routes)
 * get a header + back affordance, which matters most in installed-PWA standalone
 * mode where there's no browser back button.
 *
 * Mirrors AppShell's header/safe-area scaffolding; the sibling drill-in screens
 * (ApplicationStatus/ApplicationOfInterest) render an equivalent shell inline and
 * can migrate onto this later.
 *
 * The back row is page-specific, so routes that want their own title (and a
 * fixed destination for deep links, where there's no history to pop) pass it on
 * the layout element — see /apply in App.tsx. Child screens must NOT render
 * their own Header/BackBar on top of this, or the chrome doubles up.
 */
export function StandaloneShell({
  backLabel = 'common.back',
  backTo,
}: {
  /** Message key for the back row's label — the current page's title. */
  backLabel?: MessageKey
  /** Optional fixed back destination; defaults to the previous history entry. */
  backTo?: string
} = {}) {
  useThemeColor('#454548') // match the gray-900 header

  const { t } = useI18n()

  return (
    <div className="min-h-dvh bg-gray-50">
      <Header />
      <main className="min-h-dvh">
        <HeaderSpacer />
        <div className="mx-auto w-full max-w-screen-sm md:max-w-4xl">
          <BackBar label={t(backLabel)} to={backTo} />
          <Outlet />
        </div>
      </main>
    </div>
  )
}
